import React, { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useLocalization } from "../hooks/useLocalization";
import { usePermissions } from "../hooks/usePermissions";
import { useSidebarState } from "../hooks/useSidebarState";
import { useActionCenterCount } from "../hooks/useActionCenterCount";
import { useSystemJobsFailureCount } from "../hooks/useSystemJobsFailureCount";
import { buildSidebarSections, findActiveSectionId } from "../lib/sidebarConfig";
import { BrandLogo } from "./BrandLogo";

const SidebarLink = ({ item, collapsed, onNavigate, badgeCount, badgeVariant = "danger" }) => {
  const location = useLocation();
  const isCurrent = item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const handleClick = (e) => {
    if (isCurrent) {
      // Already on this page - refresh its data instead of a no-op navigation, and still
      // close the mobile drawer (matches every other nav click's behavior).
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("yoko:refresh-route"));
      onNavigate?.();
    } else {
      onNavigate?.();
    }
  };

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={handleClick}
      data-testid={item.testId}
      title={collapsed ? item.label : undefined}
      className={() =>
        `group relative flex items-center gap-3 rounded-r-lg border-l-4 px-3 py-2 text-sm font-medium transition-colors ${
          isCurrent
            ? "border-red-600 bg-slate-100 text-slate-900"
            : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-900"
        } ${collapsed ? "justify-center px-0" : ""}`
      }
    >
      <item.icon size={18} className={isCurrent ? "text-red-600 shrink-0" : "text-slate-500 shrink-0 group-hover:text-slate-600"} />
      {!collapsed && <span className="truncate flex-1">{item.label}</span>}
      {!!badgeCount && (
        <span
          data-testid={badgeVariant === "warning" ? "system-jobs-failure-badge" : "action-center-unread-badge"}
          className={`shrink-0 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${badgeVariant === "warning" ? "bg-amber-500" : "bg-red-600"} ${collapsed ? "absolute top-0.5 right-0.5" : ""}`}
        >
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </NavLink>
  );
};

const SidebarSection = ({ section, collapsed, onNavigate, actionCenterCount, jobFailureCount }) => {
  const { isExpanded, toggleSection } = useSidebarState();
  const expanded = collapsed || isExpanded(section.id);

  const badgeFor = (to) => {
    if (to === "/management/action-center") return { count: actionCenterCount, variant: "danger" };
    if (to === "/management/system/jobs") return { count: jobFailureCount, variant: "warning" };
    return { count: 0, variant: "danger" };
  };

  return (
    <div className="mb-1">
      {!collapsed && (
        <button
          type="button"
          onClick={() => toggleSection(section.id)}
          data-testid={`sidebar-section-toggle-${section.id}`}
          className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-600 transition-colors"
        >
          {section.label}
          <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`} />
        </button>
      )}
      {expanded && (
        <nav className="flex flex-col gap-0.5">
          {section.items.map((item) => {
            const badge = badgeFor(item.to);
            return <SidebarLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} badgeCount={badge.count} badgeVariant={badge.variant} />;
          })}
        </nav>
      )}
    </div>
  );
};

export const AppSidebar = ({ className = "", onNavigate, mobile = false }) => {
  const { t } = useLocalization();
  const perm = usePermissions();
  const { collapsed, setCollapsed, expandSection, expandAllSections, collapseAllSections } = useSidebarState();
  const location = useLocation();
  const sections = buildSidebarSections(perm, t);
  const effectiveCollapsed = mobile ? false : collapsed;
  const actionCenterCount = useActionCenterCount(perm.canViewManagerDigest);
  const jobFailureCount = useSystemJobsFailureCount(perm.canViewSystemJobs);

  useEffect(() => {
    const activeId = findActiveSectionId(sections, location.pathname);
    if (activeId) expandSection(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <aside
      data-testid="app-sidebar"
      className={`flex flex-col min-h-screen bg-white border-r border-slate-200 transition-[width] duration-200 ${
        effectiveCollapsed ? "w-[76px]" : "w-64"
      } ${className}`}
    >
      <div className={`flex items-center h-16 border-b border-slate-100 px-4 ${effectiveCollapsed ? "justify-center px-0" : "justify-between"}`}>
        <BrandLogo collapsed={effectiveCollapsed} onNavigate={onNavigate} />
        {!mobile && !effectiveCollapsed && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300 whitespace-nowrap ml-2">Ops</p>
        )}
      </div>
      {!effectiveCollapsed && (
        <div className="flex items-center justify-end gap-1.5 px-3 py-1.5 border-b border-slate-100">
          <button
            type="button"
            data-testid="sidebar-expand-all"
            onClick={() => expandAllSections(sections.map((s) => s.id))}
            className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-700 transition-colors"
          >
            {t("common.expandAll")}
          </button>
          <span className="text-slate-200 text-[10px]" aria-hidden="true">|</span>
          <button
            type="button"
            data-testid="sidebar-collapse-all"
            onClick={() => collapseAllSections()}
            className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-700 transition-colors"
          >
            {t("common.collapseAll")}
          </button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-3 px-2">
        {sections.map((section) => (
          <SidebarSection key={section.id} section={section} collapsed={effectiveCollapsed} onNavigate={onNavigate} actionCenterCount={actionCenterCount} jobFailureCount={jobFailureCount} />
        ))}
      </div>
      {!mobile && (
        <button
          type="button"
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? t("common.expandSidebar") : t("common.collapseSidebar")}
          className="flex items-center justify-center gap-2 h-11 border-t border-slate-100 text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors text-xs font-semibold"
        >
          {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> {t("common.collapseSidebar")}</>}
        </button>
      )}
    </aside>
  );
};

